/**
 * Cosa va storto dopo la vendita (Davide 29/10/2026): i motivi tipici dei
 * clienti, i pezzi di ricambio Itamoby/Ityhome e i prodotti con piu' problemi.
 */

import type { BiProblemi } from "../biApi";
import { eur, intero, n, nomeFornitore, perc } from "../biTesti";
import { Riquadro } from "./Riquadro";

// ── Motivi dei clienti ───────────────────────────────────────────────────────

export function MotiviClienti({ p }: { p: BiProblemi }) {
  const totale = p.motivi.reduce((s, m) => s + n(m.threads), 0);
  const massimo = Math.max(1, ...p.motivi.map((m) => n(m.threads)));
  return (
    <Riquadro
      titolo="Perché scrivono i clienti"
      spiegazione={{
        titolo: "Perché scrivono i clienti",
        testo:
          "Il motivo che il cliente sceglie quando scrive su Leroy Merlin, Maisons du Monde e Vente-Unique, per gli ordini del periodo. Le richieste di fattura non sono un problema e stanno a parte. Gli altri canali scrivono testo libero: si contano, ma senza motivo.",
        incerto:
          n(p.without_reason) > 0
            ? `${intero(p.without_reason)} conversazioni di altri canali senza motivo scelto: non sono in questo elenco.`
            : undefined,
      }}
    >
      {p.motivi.length === 0 ? (
        <p className="text-[13px]">Nessun messaggio con motivo nel periodo.</p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {p.motivi.map((m) => (
            <li key={m.categoria} className="text-[13px]">
              <span className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate font-bold">{m.categoria}</span>
                <span className="shrink-0 font-semibold tabular-nums">
                  {intero(m.threads)}
                  <span className="ml-1.5 text-[12px] font-semibold opacity-70">
                    {perc((n(m.threads) / Math.max(1, totale)) * 100, 0)}
                  </span>
                </span>
              </span>
              <span className="mt-1 block h-[6px] overflow-hidden rounded-full bg-paper-2">
                <span
                  className="block h-full rounded-full bg-ink"
                  style={{ width: `${(n(m.threads) / massimo) * 100}%` }}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-[12px] font-semibold">
        {intero(p.invoice_requests)} richieste di fattura · {intero(p.without_reason)} conversazioni
        senza motivo
      </p>
    </Riquadro>
  );
}

// ── Pezzi di ricambio ────────────────────────────────────────────────────────

export function PezziRicambio({ p }: { p: BiProblemi }) {
  const r = p.ricambi;
  return (
    <Riquadro
      titolo="Pezzi che si rompono"
      spiegazione={{
        titolo: "Pezzi che si rompono",
        testo:
          "I pezzi rimandati da Itamoby e Ityhome, letti dalle loro fatture di ricambio («rif. sost»), per data della fattura. Imballi e bancali non contano. Vale per tutti i canali: il filtro canale qui non si applica.",
        conto: [
          { etichetta: "Fatture di ricambio", valore: intero(r.invoices) },
          { etichetta: "Pezzi", valore: intero(r.pieces) },
          { etichetta: "Costo senza IVA", valore: eur(r.cost, 2) },
        ],
        incerto:
          n(r.invoices_without_order) > 0
            ? `${intero(r.invoices_without_order)} fatture non portano a un ordine solo: i pezzi contano qui, ma non nei prodotti.`
            : undefined,
      }}
    >
      {p.pezzi.length === 0 ? (
        <p className="text-[13px]">Nessun ricambio nel periodo.</p>
      ) : (
        <ol className="divide-y divide-line">
          {p.pezzi.slice(0, 10).map((x, i) => (
            <li
              key={`${x.supplier}-${x.part}`}
              className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-3 py-2 text-[13px]"
            >
              <span className="font-bold tabular-nums">{i + 1}</span>
              <span className="min-w-0">
                <span className="block truncate font-bold">{x.part}</span>
                <span className="block text-[12px] font-semibold">
                  {nomeFornitore(x.supplier)} · {eur(x.cost, 2)}
                </span>
              </span>
              <span className="text-right font-bold tabular-nums">{intero(x.pieces)} pz</span>
            </li>
          ))}
        </ol>
      )}
    </Riquadro>
  );
}

// ── Prodotti con piu' problemi ───────────────────────────────────────────────

function Conteggio({ nomi, valore }: { nomi: [string, string]; valore: number }) {
  if (n(valore) === 0) return null;
  return (
    <span className="rounded-full bg-paper-2 px-1.5">
      {intero(valore)} {n(valore) === 1 ? nomi[0] : nomi[1]}
    </span>
  );
}

export function ProdottiProblemi({ p }: { p: BiProblemi }) {
  return (
    <Riquadro
      titolo="Prodotti con più problemi"
      spiegazione={{
        titolo: "Prodotti con più problemi",
        testo:
          "Gli articoli degli ordini del periodo con un reso, una sostituzione, un ricambio del fornitore o un messaggio del cliente su un prodotto rotto, difettoso, incompleto o sbagliato. Un ordine con piu' articoli conta su ognuno.",
        conto: [{ etichetta: "Quota", valore: "ordini con problemi ÷ ordini venduti" }],
      }}
    >
      {p.prodotti.length === 0 ? (
        <p className="text-[13px]">Nessun problema nel periodo.</p>
      ) : (
        <ol className="divide-y divide-line">
          {p.prodotti.slice(0, 10).map((x, i) => (
            <li
              key={x.sku}
              className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-3 py-2 text-[13px]"
            >
              <span className="font-bold tabular-nums">{i + 1}</span>
              <span className="min-w-0">
                <span className="block truncate font-bold">{x.title || x.sku}</span>
                <span className="flex flex-wrap items-center gap-1.5 text-[12px] font-semibold">
                  <span className="font-mono">{x.sku}</span>
                  <Conteggio nomi={["reso", "resi"]} valore={x.returns} />
                  <Conteggio nomi={["sostituzione", "sostituzioni"]} valore={x.replacements} />
                  <Conteggio nomi={["ricambio", "ricambi"]} valore={x.spare_parts} />
                  <Conteggio nomi={["reclamo", "reclami"]} valore={x.complaints} />
                </span>
              </span>
              <span className="text-right font-bold tabular-nums">
                {intero(x.problem_orders)} su {intero(x.orders)}
                <span className="block text-[11.5px] font-semibold">
                  {perc((n(x.problem_orders) / Math.max(1, n(x.orders))) * 100, 0)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </Riquadro>
  );
}
